
import Vue, { PropType, computed, ComputedRef } from 'vue';
import { w2pPintura } from './components/W2PPintura';
import { ActionsToolbar } from './components/ActionsToolbar';
import { WizardHeader } from './components/Header';
import {
  TextPanelOptions,
  ImagePanelOptions,
  HomePanel,
} from './components/Panels';
import { OrderDetails } from './components/OrderDetails';
import { PositionsManagement } from './components/PositionsManagement';
import { PreviewSection } from './components/PreviewSection';
import PreviewPdf from './components/PreviewSection/components/PreviewPdf.vue';

import {
  PANELS_WIDTH,
  ICONS_TOOLBAR_WIDTH,
  POSITIONS_MANAGER_WIDTH,
  HEADER_HEIGHT,
  PINTURA_MAX_MARGIN_LEFT,
  PINTURA_MAX_MARGIN_RIGHT,
  ORDER_DETAILS_WIDTH,
  HOME_PANEL,
  TEXT_PANEL,
  IMAGE_PANEL,
} from './constants';
import { ProductPrintPositionAddModel } from '@/typings';
import {
  PdpChoicesSetterType,
  PriceDetailsSetterTypeV2,
} from '@/setters/typings';
import { LogoOnlyArtwork } from '../import-wizard/typings';
import { SupportedLangCodes } from '@/constants/languageCodes';
import isValidLanguageValidator from '@/validators/langLocaleValidator';
import { pdpChoices } from '@/setters';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import {
  mapPositionsFromPdp,
  mapProductPrintPositions,
  mapSelectedVariant,
} from '@/utils/productMapper';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useNotificationStore } from '@/stores/v2/notification';
import { useStore as useMirrorStore } from '@/stores/v2/mirror';
import { useStore as useColorsManagementStore } from '@/stores/v2/colorsManagement';
import { useStore as useHistoryStore } from '@/stores/v2/history';
import { mapActions, mapState, mapStores } from 'pinia';
import { createStateSnapshot } from '@/utils/historyHelper';
import { AnnotationShape } from './typings/common';
import LogoLibrary from '../logo-library/TheApp.vue';
import { ArtworkPayload } from '../import-wizard/typings/pintura';
import { generateEventPayloadV2 } from '@/utils/generatePriceParamsEventPayload';
import CropEditor from '../import-wizard-v2/components/CropEditor/CropEditor.vue';
import { NotificationsManagement } from './components/NotificationsManagement';
import { Personalization } from '@midocean/webapps-shared-domain';
import { useStore as useLangStore } from '@/stores/v2/lang';
import { useStore as useGlobalStore } from '@/stores/v2/global';
import { CvLoading } from '@carbon/vue';

export default Vue.extend({
  components: {
    WizardHeader,
    ActionsToolbar,
    PositionsManagement,
    PreviewSection,
    w2pPintura,
    TextPanelOptions,
    HomePanel,
    ImagePanelOptions,
    LogoLibrary,
    CropEditor,
    OrderDetails,
    CvLoading,
    PreviewPdf,
    NotificationsManagement,
  },
  props: {
    languageCode: {
      type: String as PropType<SupportedLangCodes>,
      required: false,
      default: 'none',
      validator: isValidLanguageValidator,
    },
    personalizationId: {
      type: String,
      required: false,
      default() {
        return '';
      },
    },
    priceDetails: {
      type: Object as PropType<PriceDetailsSetterTypeV2>,
      required: true,
    },
    pdpChoices,
    sapCustomerId: {
      type: String,
      required: true,
    },
    tok: {
      type: String,
      required: true,
    },
    isDirectApproval: {
      type: Boolean,
      required: false,
      default: false,
    },
    isFirstTimeOpened: {
      type: Boolean,
      required: false,
      default: false,
    },
    isConceptDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    isOrderDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    printPositions: {
      type: [] as PropType<ProductPrintPositionAddModel[]>,
      required: false,
      default() {
        return [];
      },
    },
    customerCompanyLogoUrl: {
      type: String,
      required: false,
      default: '',
    },
    logoOnlyArtwork: {
      type: [] as PropType<LogoOnlyArtwork[]>,
      required: false,
      default: () => {
        return [];
      },
    },
  },
  provide(): {
    $pdpChoices: ComputedRef<PdpChoicesSetterType>;
    $sapCustomerId: ComputedRef<string>;
    $isEditMode: ComputedRef<boolean>;
    $personalization: ComputedRef<Personalization>;
    $isDirectApproval: ComputedRef<boolean>;
    $isConceptDisabled: ComputedRef<boolean>;
    $isOrderDisabled: ComputedRef<boolean>;
    $customerCompanyLogoUrl: ComputedRef<string>;
  } {
    return {
      $pdpChoices: computed(() => this.pdpChoices),
      $sapCustomerId: computed(() => this.sapCustomerId),
      $isEditMode: computed(() => this.isEditMode),
      $personalization: computed(() => this.personalization),
      $isDirectApproval: computed(() => this.isDirectApproval),
      $isOrderDisabled: computed(() => this.isOrderDisabled),
      $isConceptDisabled: computed(() => this.isConceptDisabled),
      $customerCompanyLogoUrl: computed(() => this.customerCompanyLogoUrl),
    };
  },

  data() {
    return {
      rootElement: null as unknown as HTMLElement,
      rootHeight: 0,
      rootWidth: 0,
      ICONS_TOOLBAR_WIDTH,
      PANELS_WIDTH,
      POSITIONS_MANAGER_WIDTH,
      HEADER_HEIGHT,
      PINTURA_MAX_MARGIN_LEFT,
      PINTURA_MAX_MARGIN_RIGHT,
      activePanel: HOME_PANEL,
      isModalOpen: false,
      initializeCropEditor: false,
      pinturaPreviewImage: null as null | string,
      isPinturaExplicitDisableFlag: false,
      isConcept: false,
      showPreview: false,
      triggerSmallDetailsCheck: false,
    };
  },
  computed: {
    ...mapState(useGlobalStore, ['isLoaderActive']),
    ...mapState(useShapeStore, ['personalization', 'activeShapeId', 'shapes']),
    ...mapState(useLangStore, ['languageInUse']),
    ...mapStores(
      useProductStore,
      useShapeStore,
      useColorsManagementStore,
      useMirrorStore,
      useHistoryStore,
      useNotificationStore
    ),
    ...mapState(useColorsManagementStore, ['availablePmsColors']),
    ...mapState(useProductStore, [
      'activePosition',
      'area',
      'rotation',
      'unavailablePrintPositions',
      'selectedPrintPosition',
      'selectedVariant',
      'positionsSetFromProps',
      'data',
      'isProductPrintable',
      'editedPositions',
      'isLaserConverter',
      'isDebossingTechnique',
      'isSmallDetailsTechnique',
      'previousManagedPrintPositionId',
    ]),

    isEditMode(): boolean {
      return this.personalizationId !== '';
    },

    orderDetailsLeft() {
      let orderDetailsLeft = 0;

      const pinturaContainerWidth =
        this.rootWidth -
        ICONS_TOOLBAR_WIDTH -
        PANELS_WIDTH -
        POSITIONS_MANAGER_WIDTH -
        25;
      if (pinturaContainerWidth > ORDER_DETAILS_WIDTH) {
        orderDetailsLeft =
          (pinturaContainerWidth - ORDER_DETAILS_WIDTH) / 2 +
          ICONS_TOOLBAR_WIDTH +
          PANELS_WIDTH;
      } else {
        orderDetailsLeft = ICONS_TOOLBAR_WIDTH + PANELS_WIDTH;
      }

      return `${orderDetailsLeft}px`;
    },

    modalHandlers() {
      return {
        beforeClose: () => {
          this.isModalOpen = false;
        },
      };
    },

    pinturaContainer(): {
      width: string;
      height: string;
    } {
      const containerWidth =
        this.rootWidth -
        ICONS_TOOLBAR_WIDTH -
        PANELS_WIDTH -
        POSITIONS_MANAGER_WIDTH -
        25;

      const containerHeight = this.rootHeight - HEADER_HEIGHT - 200;

      return {
        width: `${containerWidth}px`,
        height: `${containerHeight}px`,
      };
    },

    activePanelLabel(): 'Image' | 'Text' | 'Working area' {
      if (this.activePanel === IMAGE_PANEL) {
        return 'Image';
      } else if (this.activePanel === TEXT_PANEL) {
        return 'Text';
      }
      return 'Working area';
    },
  },

  watch: {
    async pdpChoices() {
      // TO DO : Cover redirect from Logo Only to wizard flow
      this.initializeStateForRegularMode();
    },
  },

  async mounted() {
    this.$nextTick(() => {
      const subtarget = document.querySelector('w2p-import-wizard-wrapper');

      const element =
        subtarget && subtarget.shadowRoot
          ? subtarget.shadowRoot.querySelector('.import-wizard-v2')
          : document.body.querySelector('.import-wizard-v2');

      if (element) {
        this.rootElement = element as HTMLElement;
        this.rootHeight = (element as HTMLElement).offsetHeight;
        this.rootWidth = (element as HTMLElement).offsetWidth;
      }

      window.addEventListener('resize', this.onResize);
    });

    await this.getAvailablePmsColors();

    if (this.personalizationId !== '') {
      await this.initializeStateForEditMode();
    } else {
      await this.initializeStateForRegularMode();
    }

    const snapshot = createStateSnapshot();
    this.recordHistory(snapshot);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },

  methods: {
    ...mapActions(useShapeStore, [
      'getPersonalization',
      'initializeStateFromPersonalization',
      'addTextPayload',
      'deselectShape',
      'selectShape',
      'addArtworkPayload',
    ]),
    ...mapActions(useProductStore, [
      'getProduct',
      'setUnavailablePrintPositions',
      'setSelectedVariant',
      'setSelectedVariants',
      'setSelectedPrintPosition',
    ]),
    ...mapActions(useMirrorStore, [
      'initializeMirrorSettingsFromProof',
      'initializeMirrorSetting',
    ]),
    ...mapActions(useColorsManagementStore, ['getAvailablePmsColors']),
    ...mapActions(useHistoryStore, ['recordHistory']),
    ...mapActions(useNotificationStore, ['runValidators']),
    ...mapActions(useGlobalStore, ['setIsProcessingOrder']),

    getPreviewImage() {
      return this.selectedVariant !== undefined
        ? (this.selectedVariant.images?.find(
            (i) => i.printPositionID === this.selectedPrintPosition.id
          )?.imageWebUrl as string)
        : null;
    },

    onResize() {
      this.rootHeight = this.rootElement.offsetHeight;
      this.rootWidth = this.rootElement.offsetWidth;
    },

    changeActivePanel(panel: string) {
      this.deselectShape();
      this.activePanel = panel;
    },

    async initializeStateForRegularMode() {
      if (this.pdpChoices.masterProductSKU !== '') {
        await this.getProduct(this.pdpChoices.masterProductSKU);

        const mappedPositions = mapPositionsFromPdp(this.pdpChoices);
        this.setUnavailablePrintPositions(mappedPositions);

        if (mappedPositions) {
          this.setSelectedPrintPosition(mappedPositions[0].positionId);
        }

        this.setSelectedVariants(this.pdpChoices.productVariants);

        const variant = mapSelectedVariant(
          this.pdpChoices.productVariants[0].variantColor
        );
        if (variant) this.setSelectedVariant(variant);

        this.initializeMirrorSetting();

        this.runValidators();
      }
    },

    async initializeStateForEditMode() {
      await this.getPersonalization(this.personalizationId);

      if (this.personalization) {
        await this.getProduct(this.personalization.productSKU);

        const mappedPositions = mapProductPrintPositions();

        await this.initializeStateFromPersonalization();
        this.setUnavailablePrintPositions(mappedPositions);

        if (mappedPositions) {
          this.setSelectedPrintPosition(mappedPositions[0].positionId);
        }

        this.initializeMirrorSettingsFromProof(this.personalization.proofs[0]);

        const variant = mapSelectedVariant(
          this.personalization.productVariants[0].variantColor
        );
        if (variant) {
          this.setSelectedVariant(variant);
          this.setSelectedVariants(this.personalization.productVariants);
        }

        this.runValidators();
      }
    },
    async addToImageDecoration(shape: AnnotationShape) {
      if (shape.kind === 'Text') {
        const positionId = this.productStore.selectedPrintPosition.id;

        await this.addTextPayload({ ...shape, kind: 'Text' }, positionId);

        if (shape.w2pText.text !== '') {
          const snapshot = createStateSnapshot();
          this.recordHistory(snapshot);
        }
      }

      // const eventPayload = generateEventPayload();

      // this.$emit('wizard:price-params-changed', {
      //   productPrintPositions: eventPayload,
      // });
    },
    openModal() {
      /**
       * Disable pintura while the logo library modal is open
       */
      this.isModalOpen = true;

      /**
       * Open logo library modal
       */
      this.$modal.show('logo-library-modal', { foo: 'bar' });
    },
    openCropEditor() {
      this.initializeCropEditor = true;
    },
    setActiveShapeId(id: string) {
      this.selectShape(id);
    },
    async onImportArtwork(payload: ArtworkPayload) {
      await this.addArtworkPayload(
        payload,
        this.productStore.selectedPrintPosition.id
      );
      this.setActiveShapeId(payload.id);

      const eventPayload = generateEventPayloadV2();
      this.$emit('wizard:price-params-changed', {
        productPrintPositions: eventPayload,
      });

      this.runValidators();

      this.$modal.hide('logo-library-modal');
    },
    beforeSelectShape(target: AnnotationShape) {
      if (target.kind === 'Text') {
        this.activePanel = TEXT_PANEL;
      } else if (target.kind === 'Logo') {
        this.activePanel = IMAGE_PANEL;
      }
      this.selectShape(target.id);
    },
  },
});
